import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Slider,
  Stack,
  Button,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/ai-crm`,
  headers: { 'Content-Type': 'application/json' },
});
apiClient.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

interface AllocationView {
  name: string | null;
  email: string;
  role: string;
  allocated: number;
  used: number;
  remaining: number;
}

interface CreditUsageResponse {
  plan: string | null;
  poolTotal: number;
  poolUsed: number;
  poolRemaining: number;
  periodStart: string | null;
  periodEnd: string | null;
  allocations: Record<string, AllocationView>;
  superAdminQuota: { allocated: number; used: number; remaining: number };
  superAdminEnabled: boolean;
}

const fetchUsage = async (): Promise<CreditUsageResponse> => {
  const res = await apiClient.get<{ success: boolean; data: CreditUsageResponse }>(
    '/credits/usage',
  );
  return res.data.data;
};

const fetchTeam = async (): Promise<Array<{ id: string; name: string | null; email: string; role: string }>> => {
  const res = await apiClient.get<{ success: boolean; data: any[] }>('/ai-assistant/team-members');
  return res.data.data ?? [];
};

const saveAllocations = async (allocations: Record<string, number>) => {
  await apiClient.put('/credits/allocation', { allocations });
};

const CreditAllocationPanel: React.FC = () => {
  const qc = useQueryClient();
  const usageQ = useQuery({ queryKey: ['credits-usage'], queryFn: fetchUsage });
  const teamQ = useQuery({ queryKey: ['team-members'], queryFn: fetchTeam });

  // Local edit state — userId → allocated count
  const [draft, setDraft] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!usageQ.data) return;
    const next: Record<string, number> = {};
    Object.entries(usageQ.data.allocations).forEach(([uid, v]) => {
      next[uid] = v.allocated;
    });
    setDraft(next);
  }, [usageQ.data]);

  const poolTotal = usageQ.data?.poolTotal ?? 0;
  const assigned = useMemo(
    () => Object.values(draft).reduce((acc, v) => acc + (v ?? 0), 0),
    [draft],
  );
  const superAdminRemainder = Math.max(0, poolTotal - assigned);
  const isOverAllocated = assigned > poolTotal;

  const saveMut = useMutation({
    mutationFn: saveAllocations,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credits-usage'] }),
  });

  if (usageQ.isLoading) return <Box p={3}><LinearProgress /></Box>;
  if (!usageQ.data) return <Alert severity="error">Failed to load credit usage</Alert>;
  if (!usageQ.data.superAdminEnabled) {
    return (
      <Alert severity="info">
        Per-user lead allocation is available on the Business Plus plan.
      </Alert>
    );
  }

  const team = (teamQ.data ?? []).filter((u) => u.role !== 'SUPER_ADMIN' && u.role !== 'ADMIN');
  const adminSelf = (teamQ.data ?? []).find((u) => u.role === 'ADMIN' || u.role === 'SUPER_ADMIN');

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Lead credit pool
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {usageQ.data.periodStart && usageQ.data.periodEnd && (
                  <>
                    Billing period {new Date(usageQ.data.periodStart).toLocaleDateString()} —{' '}
                    {new Date(usageQ.data.periodEnd).toLocaleDateString()}
                  </>
                )}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Chip label={`Pool: ${poolTotal}`} variant="outlined" />
              <Chip label={`Assigned: ${assigned}`} color={isOverAllocated ? 'error' : 'primary'} />
              <Chip label={`You keep: ${superAdminRemainder}`} color="success" />
            </Stack>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, poolTotal > 0 ? (usageQ.data.poolUsed / poolTotal) * 100 : 0)}
            sx={{ mt: 2, height: 10, borderRadius: 5 }}
          />
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            {usageQ.data.poolUsed} / {poolTotal} reveals used this period
          </Typography>
          {isOverAllocated && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Allocations exceed the pool by {assigned - poolTotal}. Reduce before saving.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Per-user allocation
          </Typography>
          {team.length === 0 ? (
            <Alert severity="info">
              No org users to allocate to. Invite users in Settings &rsaquo; Team first.
            </Alert>
          ) : (
            <Stack spacing={3}>
              {team.map((u) => {
                const value = draft[u.id] ?? 0;
                const used = usageQ.data!.allocations[u.id]?.used ?? 0;
                return (
                  <Box key={u.id}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={600}>
                        {u.name || u.email}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {u.email}
                        </Typography>
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Chip size="small" label={`Allocated ${value}`} variant="outlined" />
                        <Chip
                          size="small"
                          label={`Used ${used}`}
                          color={used >= value && value > 0 ? 'error' : 'default'}
                        />
                      </Stack>
                    </Stack>
                    <Slider
                      value={value}
                      onChange={(_, v) => setDraft((d) => ({ ...d, [u.id]: v as number }))}
                      min={0}
                      max={poolTotal}
                      step={1}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                );
              })}

              {adminSelf && (
                <>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {adminSelf.name || adminSelf.email} <Chip size="small" label="SuperAdmin" sx={{ ml: 1 }} />
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Keeps whatever isn't allocated to others
                      </Typography>
                    </Box>
                    <Chip
                      color="success"
                      label={`${superAdminRemainder} / ${poolTotal}`}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>
                </>
              )}

              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button
                  variant="text"
                  onClick={() => {
                    if (!usageQ.data) return;
                    const next: Record<string, number> = {};
                    Object.entries(usageQ.data.allocations).forEach(([uid, v]) => {
                      next[uid] = v.allocated;
                    });
                    setDraft(next);
                  }}
                  disabled={saveMut.isPending}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  disabled={isOverAllocated || saveMut.isPending}
                  onClick={() => saveMut.mutate(draft)}
                >
                  {saveMut.isPending ? 'Saving…' : 'Save allocations'}
                </Button>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreditAllocationPanel;
